import { useLoading } from "@/Contexts/LoadingContext";
import { useAtom, useSetAtom } from "jotai/index";
import { usePage } from "@inertiajs/inertia-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Inertia } from "@inertiajs/inertia";
import {
  LabeledInput,
  LabeledMultiSelectAutocomplete,
  LabeledSelect,
  LabeledSelectAutocomplete,
} from "@/Components/Form";
import { Globe, MapPin, Search } from "react-feather";
import { AccordionItem, Dialog } from "@/Components";
import { LoaderCircle } from "@/Icons";
import {
  List,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
} from "react-virtualized";
import { EMISSION_FACTOR_DATATYPES } from "@/Enums/emission_factor_datatypes";
import { GAS_OPTIONS } from "@/Enums/gas_options";
import { isEmissionFactorExplorerOpenAtom } from "@/Components/Questions/QuestionBaseForm";
import { focusAtom } from "jotai-optics";
import { Tag } from "@/Components/Tag";
import { EMISSION_FACTOR_DATASETS } from "@/Enums/emission_factor_datasets";

function EmissionFactorExplorerSidebar({ atom }) {
  const { isLoaded, setIsLoaded } = useLoading();

  const [openModal, setOpenModal] = useAtom(isEmissionFactorExplorerOpenAtom);

  const { props } = usePage();

  const [factors, setFactors] = useState([]);
  const [openAccordions, setOpenAccordions] = useState([]);

  // on mount, reload only library_factors
  useEffect(() => {
    if (openModal !== null) {
      Inertia.reload({
        only: ["factors"],
      });
    }
  }, [openModal]);

  useEffect(() => {
    if (
      (!isLoaded || factors.length !== props?.factors?.data?.length || 0) &&
      props?.factors?.data
    ) {
      setFactors(props.factors.data);
      setIsLoaded(true);
    }
  }, [props.factors]);

  const dataTypeOptions = Object.values(EMISSION_FACTOR_DATATYPES).map((d) => ({
    label: d.label,
    value: d.value,
  }));

  const dataAccuracyOptions = [
    { label: "Very high", value: "5" },
    { label: "High", value: "10" },
    { label: "Fair", value: "15" },
    { label: "Low", value: "20" },
    { label: "Very low", value: "25" },
  ];

  const [showFilters, setShowFilters] = useState(false);

  const [search, setSearch] = useState("");
  const [dataTypes, setDataTypes] = useState(
    dataTypeOptions.map((t) => t.value),
  );
  const [dataAccuracy, setDataAccuracy] = useState("");
  const [regionalRestriction, setRegionalRestriction] = useState("");

  const ref = useRef(null);

  const filteredFactors = factors.filter((factor) => {
    const hasDataTypes = dataTypes.includes(factor.datatype);
    let hasDataAccuracy = true;
    if (dataAccuracy) {
      hasDataAccuracy = factor.scientific_uncertainty <= dataAccuracy;
    }

    let isInSearch = true;
    if (search) {
      const keywords = search.split(" ");

      isInSearch = keywords.every((keyword) =>
        factor.name.toLowerCase().includes(keyword),
      );
    }

    let isInRegionalRestriction = true;
    if (regionalRestriction) {
      const keywords = regionalRestriction.split(" ");

      isInRegionalRestriction = keywords.every((keyword) =>
        factor.regional_restrictions?.toLowerCase()?.includes(keyword),
      );
    }

    return (
      isInSearch && isInRegionalRestriction && hasDataTypes && hasDataAccuracy
    );
  });

  if (openModal === null) return null;

  const handleAccordionToggle = (id) => {
    setOpenAccordions((prevOpenAccordions) => {
      if (prevOpenAccordions.includes(id)) {
        return prevOpenAccordions.filter((accordionId) => accordionId !== id);
      } else {
        return [...prevOpenAccordions, id];
      }
    });
  };

  const cache = new CellMeasurerCache({
    defaultHeight: 70, // Default height for rows
    fixedWidth: true,
  });

  function Row({ index, key, parent, style }) {
    const factor = filteredFactors[index];
    return (
      <CellMeasurer
        key={key}
        cache={cache}
        parent={parent}
        columnIndex={0}
        rowIndex={index}
      >
        {({ measure, registerChild }) => (
          <div ref={registerChild} style={{ ...style, paddingBottom: "8px" }}>
            <AccordionItem
              key={factor.id}
              title={<AccordionTitle factor={factor} />}
              titleStyle=""
              marginStyle=""
              className="border"
              titleright={
                <AccordionAction
                  factor={factor}
                  openModal={openModal}
                  setOpenModal={setOpenModal}
                  atom={atom}
                />
              }
              icon={
                factor?.global ? (
                  <Globe
                    size={18}
                    className="mr-2 mt-1 shrink-0 self-start"
                    strokeWidth={1.5}
                  />
                ) : null
              }
              onResizeTick={measure}
              onToggle={() => {
                handleAccordionToggle(factor.id);
                measure();
              }}
              defaultActive={openAccordions.includes(factor.id)}
            >
              <EmissionFactorDetails
                factor={factor}
                openModal={openModal}
                setOpenModal={setOpenModal}
                atom={atom}
              />
            </AccordionItem>
          </div>
        )}
      </CellMeasurer>
    );
  }

  return (
    <Dialog
      openModal={openModal !== null}
      closeModal={() => setOpenModal(null)}
      title="Add emission factor"
    >
      <div
        className="flex w-full flex-col flex-wrap gap-4 sm:flex-row"
        ref={ref}
      >
        <LabeledInput
          value={search}
          type="search"
          placeholder="Search"
          containerClass="w-full"
          unit={search === "" ? <Search className="relative" /> : null}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
        />

        {showFilters && (
          <>
            <LabeledMultiSelectAutocomplete
              title="Datatypes"
              className="w-full sm:basis-1/2-gap-4"
              placeholder="Datatypes"
              options={dataTypeOptions}
              defaultValue={dataTypeOptions.map((t) => t.value)}
              onSelect={(val) => {
                const values = val.map((v) => v.value);
                setDataTypes(values);
              }}
            />

            <LabeledSelect
              title="Data accuracy"
              containerClass="w-full sm:basis-1/2-gap-4"
              placeholder="Data accuracy"
              defaultOption="Show all"
              options={dataAccuracyOptions}
              onChange={(e) => {
                setDataAccuracy(e.target.value);
              }}
            />
            <div className="relative w-full">
              <LabeledInput
                value={regionalRestriction}
                name="regional_restrictions"
                title="Regional restrictions"
                placeholder="Regional restrictions"
                className="w-full pl-10"
                onChange={(e) => {
                  setRegionalRestriction(e.target.value);
                }}
              />
              <MapPin size={18} className="absolute left-3 -mt-8" />
            </div>
          </>
        )}

        <button
          className="mb-2 text-left text-blue underline hover:no-underline"
          onClick={() => {
            setShowFilters(!showFilters);
          }}
        >
          {showFilters ? "Hide filters" : "Show filters"}
        </button>
      </div>

      {!isLoaded && (
        <div className="flex items-center justify-center">
          <LoaderCircle size={40} />
        </div>
      )}
      {isLoaded && factors.length === 0 && (
        <div className="flex items-center justify-center p-8 text-center">
          <h3 className="mx-4 text-lg font-bold text-blue-dark">
            No factors was found
          </h3>
        </div>
      )}

      {isLoaded && factors.length > 0 && (
        <div className="flex h-full w-full flex-col">
          <div className="mb-4 flex justify-between gap-4">
            <div className="w-1/2 overflow-hidden">
              <p className="text-md font-bold">
                {filteredFactors.length} results
              </p>

              {/*<div className="flex gap-2 max-sm:hidden">*/}
              {/*  <p>For:</p>*/}
              {/*<ShrinkableDashboardFilterItems*/}
              {/*  gap="gap-2"*/}
              {/*  items={[*/}
              {/*    ...tagOptions*/}
              {/*      .filter((t) => tags.includes(t.value))*/}
              {/*      .map((t) => ({*/}
              {/*        name: t.label,*/}
              {/*        html: <span>{t.label},</span>,*/}
              {/*      })),*/}
              {/*    ...typeOptions*/}
              {/*      .filter((t) => types.includes(t.value))*/}
              {/*      .map((t) => ({*/}
              {/*        name: t.label,*/}
              {/*        html: <span>{t.label},</span>,*/}
              {/*      })),*/}
              {/*  ]}*/}
              {/*/>*/}
              {/*</div>*/}
            </div>
          </div>
          {/*{filteredFactors.map((factor) => (*/}
          {/*  <AccordionItem*/}
          {/*    key={factor.id}*/}
          {/*    title={<AccordionTitle factor={factor} />}*/}
          {/*    titleStyle="text-base font-bold"*/}
          {/*    className="border"*/}
          {/*    titleright={<AccordionAction factor={factor} />}*/}
          {/*  >*/}
          {/*    <EmissionFactorDetails factor={factor} />*/}
          {/*  </AccordionItem>*/}
          {/*))}*/}
          <div className="h-full">
            <AutoSizer>
              {({ height, width }) => (
                <List
                  width={width}
                  height={height}
                  deferredMeasurementCache={cache}
                  rowHeight={cache.rowHeight}
                  rowCount={filteredFactors.length}
                  rowRenderer={Row}
                />
              )}
            </AutoSizer>
          </div>
        </div>
      )}
    </Dialog>
  );
}

export default EmissionFactorExplorerSidebar;

function AccordionTitle({ factor }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <h3 className="text-base font-bold">
        {factor.name} <span className="font-normal">{factor.unit}</span>
      </h3>

      <div className="mt-2 block w-full">
        {factor.datatype && (
          <Tag margin="mr-2">
            {
              Object.values(EMISSION_FACTOR_DATATYPES).find(
                (d) => d.value === factor.datatype,
              ).label
            }
          </Tag>
        )}
        {factor.dataset && (
          <Tag margin="mr-2">
            {
              Object.values(EMISSION_FACTOR_DATASETS).find(
                (d) => d.value === factor.dataset,
              ).label
            }
          </Tag>
        )}

        <span className="text-sm">{`${factor.scientific_uncertainty}%`}</span>
      </div>
    </div>
  );
}

function AccordionAction({
  factor,
  className = "text-blue underline hover:no-underline",
  openModal,
  setOpenModal,
  atom,
}) {
  const inputAtom = useMemo(
    () => focusAtom(atom, (optic) => optic.prop("inputs").at(openModal || 0)),
    [atom, openModal],
  );

  const [payload, setPayload] = useAtom(inputAtom);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setPayload((prev) => ({ ...prev, emission_factor_id: factor.id }));

    setOpenModal(null);
  };

  return (
    <>
      {payload?.emission_factor_id === factor.id ? (
        <p>Selected</p>
      ) : (
        <button
          className={`flex min-w-40 justify-center ${className}`}
          onClick={handleClick}
        >
          Add emission factor
        </button>
      )}
    </>
  );
}

function EmissionFactorDetails({
  factor,
  setIsActive,
  startAnimating,
  openModal,
  setOpenModal,
  atom,
}) {
  const gasOptions = [
    {
      label: "CO₂e",
      value: "co2e",
    },
    ...GAS_OPTIONS,
  ];

  const [showFullDescription, setShowFullDescription] = useState(false);

  return (
    <>
      <div className="flex flex-col gap-2">
        <p>
          <span className="font-bold">Source </span>
          {factor.source || "Unknown"}
        </p>
        <p>
          <span className="font-bold">Publication year </span>
          {factor.year || "Unknown"}
        </p>
        <p>
          <span className="font-bold">Standard </span>
          GHG Protocol
        </p>
        <p>
          <span className="font-bold">Regional restrictions </span>
          {factor.regional_restrictions || "None"}
        </p>

        {factor.description && (
          <>
            <p
              className={`mt-2 overflow-hidden ${showFullDescription ? "" : "line-clamp-1"}`}
            >
              {factor.description}
            </p>
            <button
              className="-mt-2 mb-2 text-left text-blue underline hover:no-underline"
              onClick={() => {
                setShowFullDescription(!showFullDescription);
                startAnimating();
              }}
            >
              {showFullDescription ? "Show less" : "Show more"}
            </button>
          </>
        )}

        <AccordionItem
          title="Data quality and variations"
          className="bg-[#F6F6F6]"
          titleStyle="text-base font-bold"
          onResizeTick={startAnimating}
        >
          <>
            <div className="flex flex-col gap-2">
              <p>
                <span className="font-bold">Datatype </span>
                {factor.datatype
                  ? Object.values(EMISSION_FACTOR_DATATYPES).find(
                      (d) => d.value === factor.datatype,
                    ).label
                  : "Unknown"}
              </p>
              <p>
                <span className="font-bold">Dataset </span>
                {factor.dataset
                  ? Object.values(EMISSION_FACTOR_DATASETS).find(
                      (d) => d.value === factor.dataset,
                    ).label
                  : "Unknown"}
              </p>
              <p>
                <span className="font-bold">Scientific uncertainty </span>
                +/- {factor.scientific_uncertainty}%
              </p>
              <p>
                <span className="font-bold">Verification </span>
                {factor.verified_state !== "verified" ||
                factor.verified_state !== "verified_3rd_party"
                  ? "Not verified"
                  : factor.verified_state === "verified"
                    ? "Verified by Gidås"
                    : "Verified by 3rd party"}
              </p>
            </div>
          </>
        </AccordionItem>
        <AccordionItem
          title="Calculation"
          className="bg-[#F6F6F6]"
          titleStyle="text-base font-bold"
          onResizeTick={startAnimating}
        >
          <>
            <div className="flex flex-col gap-2">
              {factor.gases.map((gas, i) => (
                <p key={i}>
                  <span className="font-bold">
                    {gasOptions.find((g) => g.value === gas.gas)?.label}{" "}
                  </span>
                  {gas.factor} kg/{factor.unit}
                </p>
              ))}
            </div>
          </>
        </AccordionItem>
      </div>

      <div className="mt-4 flex justify-end gap-4">
        <button
          className="text-blue underline hover:no-underline"
          onClick={() => {
            setIsActive(false);
            startAnimating();
          }}
        >
          Close
        </button>
        <AccordionAction
          factor={factor}
          className="btn btn-blue grow-0 p-3"
          openModal={openModal}
          setOpenModal={{ setOpenModal }}
          atom={atom}
        />
      </div>
    </>
  );
}
